import type { CSSProperties } from "react";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import type { PdfTemplateLabels } from "../../pdfReportLabels";
import type { Language, RiskLevel, ScanResponse } from "../../types";
import "./pdf-template.css";

export type PdfReportInput = {
  result: ScanResponse;
  lang: Language;
  labels: PdfTemplateLabels;
  productTitle: string;
  verticalLabel: string;
  skuLabel: string;
  scanIdShort: string;
  scannedAt: Date;
  claimsText: string;
  riskLabel: string;
  includeCover: boolean;
  logoDataUrl: string | null;
  companyName?: string;
};

type Props = PdfReportInput & {
  compact?: boolean;
  className?: string;
};

const R = 70;
const CIRC = 2 * Math.PI * R;

function countByRisk(items: ScanResponse["flaggedItems"], r: RiskLevel): number {
  return items.filter((x) => x.risk === r).length;
}

function riskChipClass(risk: RiskLevel): string {
  if (risk === "high") return "chip-critical";
  if (risk === "medium") return "chip-warning";
  return "chip-obs";
}

function riskChipLabel(risk: RiskLevel, L: PdfTemplateLabels): string {
  if (risk === "high") return L.riskCritical;
  if (risk === "medium") return L.riskWarning;
  return L.riskObservation;
}

function wordCount(text: string, lang: Language): number {
  const raw = text.trim();
  if (!raw) return 0;
  if (lang === "zh") return raw.replace(/\s/g, "").length;
  return raw.split(/\s+/).filter(Boolean).length;
}

function formatReportDate(d: Date, lang: Language): string {
  try {
    return new Intl.DateTimeFormat(lang === "zh" ? "zh-CN" : "en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 16).replace("T", " ");
  }
}

export function PdfTemplate({
  result,
  lang,
  labels,
  productTitle,
  verticalLabel,
  skuLabel,
  scanIdShort,
  scannedAt,
  claimsText,
  riskLabel,
  includeCover,
  logoDataUrl,
  companyName,
  compact,
  className = "",
}: Props) {
  const score = Math.max(0, Math.min(100, result.score));
  const dashOffset = CIRC * (1 - score / 100);
  const nHigh = countByRisk(result.flaggedItems, "high");
  const nMed = countByRisk(result.flaggedItems, "medium");
  const nLow = countByRisk(result.flaggedItems, "low");
  const wc = wordCount(claimsText, lang);
  const evidence = result.fda?.evidence ?? [];
  const rootClass = `pdf-template${compact ? " pt-compact" : ""} ${className}`.trim();

  let pageNum = includeCover ? 1 : 0;
  const nextPage = () => {
    pageNum += 1;
    return String(pageNum).padStart(2, "0");
  };

  const regulationBlocks =
    evidence.length > 0
      ? evidence.slice(0, 6).map((ev, i) => ({
          key: `ev-${i}`,
          title: ev.term || ev.product || labels.regMatchTitle,
          body: [ev.reason, ev.classification, ev.report_date].filter(Boolean).join(" · ") || "—",
          impact: labels.regEvidenceImpact,
        }))
      : [
          {
            key: "fda-201",
            title: labels.regEx1Title,
            body: labels.regEx1Body,
            impact: labels.regEx1Impact,
          },
          {
            key: "ftc-dec",
            title: labels.regEx2Title,
            body: labels.regEx2Body,
            impact: labels.regEx2Impact,
          },
          {
            key: "otc",
            title: labels.regEx3Title,
            body: labels.regEx3Body,
            impact: labels.regEx3Impact,
          },
        ];

  const coverPage = includeCover ? (
    <div className="a4-page page-break" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
        {logoDataUrl ? (
          <img className="cover-logo-img" src={logoDataUrl} alt="" />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 40,
                height: 40,
                background: "#003d9b",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <SafetyCertificateOutlined style={{ fontSize: 22 }} />
            </div>
            <span className="font-headline" style={{ fontWeight: 800, fontSize: "1.25rem", color: "#003d9b" }}>
              {labels.brandSentinel}
            </span>
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", marginTop: 48 }}>
        <div style={{ width: 96, height: 4, background: "#003d9b", marginBottom: 40 }} />
        <h1
          className="font-headline"
          style={{ fontSize: "2.75rem", fontWeight: 800, lineHeight: 1.15, color: "#051a3e", margin: "0 0 1.5rem" }}
        >
          {labels.reportTitle}
        </h1>
        <h2 style={{ fontSize: "1.5rem", fontWeight: 500, color: "#4f5f7b", margin: "0 0 4rem" }}>{productTitle || "—"}</h2>
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: "#f1f3ff",
            borderRadius: 12,
            padding: 32,
            textAlign: "left",
            display: "grid",
            gap: 16,
          }}
        >
          {[
            [labels.reportId, scanIdShort],
            [labels.generatedDate, formatReportDate(scannedAt, lang)],
            [labels.category, verticalLabel],
            [labels.company, companyName?.trim() || "—"],
            [labels.skuRef, skuLabel || "—"],
          ].map(([a, b], i) => (
            <div
              key={a}
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingBottom: i < 4 ? 12 : 0,
                borderBottom: i < 4 ? "1px solid rgba(195,198,214,0.35)" : undefined,
              }}
            >
              <span style={{ fontWeight: 500, color: "#4f5f7b" }}>{a}</span>
              <span style={{ fontFamily: i === 0 ? "ui-monospace, monospace" : "inherit" }}>{b}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ borderTop: "4px solid #ba1a1a", paddingTop: 40 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <p style={{ margin: "0 0 8px", fontSize: "0.875rem", fontWeight: 600, color: "#4f5f7b" }}>{labels.summaryAssessment}</p>
            <p className="font-headline" style={{ margin: 0, fontSize: "1.875rem", fontWeight: 800, color: "#ba1a1a" }}>
              {labels.overallRiskPrefix}：{riskLabel}
            </p>
          </div>
          <div style={{ maxWidth: 280, textAlign: "right" }}>
            <p style={{ margin: 0, fontSize: 10, fontStyle: "italic", lineHeight: 1.5, color: "#4f5f7b", opacity: 0.85 }}>
              {labels.coverDisclaimer}
            </p>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const pSummary = nextPage();

  const summaryGrid: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: 32,
  };

  return (
    <div className={rootClass}>
      {coverPage}
      <div className="a4-page page-break">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 48 }}>
          <h2 className="font-headline" style={{ margin: 0, fontSize: "1.875rem", fontWeight: 800, color: "#003d9b" }}>
            {labels.summaryTitle}
          </h2>
          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.875rem", color: "#4f5f7b" }}>P. {pSummary}</span>
        </div>
        <div style={summaryGrid}>
          <div
            style={{
              gridColumn: "span 5",
              background: "#f1f3ff",
              borderRadius: 12,
              padding: 32,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p style={{ margin: "0 0 8px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.12em", color: "#4f5f7b" }}>
              {labels.complianceScore}
            </p>
            <div className="pt-gauge-wrap">
              <svg width={160} height={160} viewBox="0 0 160 160" aria-hidden>
                <circle cx="80" cy="80" r={R} fill="transparent" stroke="rgba(195,198,214,0.45)" strokeWidth="8" />
                <circle
                  cx="80"
                  cy="80"
                  r={R}
                  fill="transparent"
                  stroke="#003d9b"
                  strokeWidth="12"
                  strokeDasharray={CIRC}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                />
              </svg>
              <span className="pt-gauge-score">{score}</span>
            </div>
            <p style={{ margin: "24px 0 0", textAlign: "center", fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.6, color: "#434654" }}>
              {result.summary || "—"}
            </p>
          </div>
          <div style={{ gridColumn: "span 7", display: "flex", flexDirection: "column", gap: 24 }}>
            <div
              style={{
                background: "#fff",
                border: "1px solid rgba(195,198,214,0.25)",
                borderRadius: 12,
                padding: 24,
              }}
            >
              <h3 style={{ margin: "0 0 16px", paddingBottom: 8, borderBottom: "1px solid rgba(195,198,214,0.12)", fontWeight: 700 }}>
                {labels.analysisScope}
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  fontSize: "0.875rem",
                }}
              >
                {[
                  [labels.targetMarket, labels.defaultMarket],
                  [labels.channel, labels.defaultChannel],
                  [labels.wordCount, wc.toLocaleString()],
                  [labels.productType, verticalLabel],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p style={{ margin: "0 0 4px", color: "#4f5f7b" }}>{k}</p>
                    <p style={{ margin: 0, fontWeight: 600 }}>{v}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 style={{ margin: "0 0 12px", fontSize: "0.875rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {labels.riskStats}
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {[
                  { label: labels.riskHigh, n: nHigh, dot: "#ba1a1a", bg: "rgba(186,26,26,0.06)", fg: "#ba1a1a" },
                  { label: labels.riskMed, n: nMed, dot: "#003d9b", bg: "rgba(0,61,155,0.06)", fg: "#003d9b" },
                  { label: labels.riskLow, n: nLow, dot: "#004e32", bg: "rgba(0,78,50,0.06)", fg: "#004e32" },
                ].map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: 16,
                      borderRadius: 8,
                      background: row.bg,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ width: 12, height: 12, borderRadius: "50%", background: row.dot }} />
                      <span style={{ fontWeight: 700, color: row.fg }}>{row.label}</span>
                    </div>
                    <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 700, color: row.fg }}>
                      {String(row.n).padStart(2, "0")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: 48,
            borderLeft: "4px solid #003d9b",
            padding: 32,
            background: "rgba(241,243,255,0.5)",
          }}
        >
          <h3 style={{ margin: "0 0 12px", fontWeight: 700 }}>{labels.execSummary}</h3>
          <p style={{ margin: 0, fontSize: "0.875rem", fontStyle: "italic", lineHeight: 1.6, color: "#4f5f7b" }}>
            {result.summary || labels.noSummary}
          </p>
        </div>
      </div>

      {result.flaggedItems.length > 0 ? (
        <div className="a4-page page-break pt-findings-section">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 48,
            }}
          >
            <h2
              className="font-headline"
              style={{ margin: 0, fontSize: "1.875rem", fontWeight: 800, color: "#003d9b" }}
            >
              {labels.findingsTitle}
            </h2>
            <span
              style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.875rem", color: "#4f5f7b" }}
            >
              P. {nextPage()}
            </span>
          </div>
          <table className="pt-table pt-findings-table">
            <thead>
              <tr>
                <th style={{ width: "26%" }}>{labels.colPhrase}</th>
                <th style={{ width: "14%" }}>{labels.colRisk}</th>
                <th style={{ width: "30%" }}>{labels.colReason}</th>
                <th style={{ width: "30%" }}>{labels.colSuggest}</th>
              </tr>
            </thead>
            <tbody>
              {result.flaggedItems.map((item, idx) => (
                <tr key={`${idx}-${item.phrase.slice(0, 24)}`}>
                  <td
                    style={{
                      fontWeight: 500,
                      fontStyle: "italic",
                      color:
                        item.risk === "high"
                          ? "#ba1a1a"
                          : item.risk === "medium"
                            ? "#003d9b"
                            : "#4f5f7b",
                    }}
                  >
                    &ldquo;{item.phrase}&rdquo;
                  </td>
                  <td>
                    <span className={`status-chip ${riskChipClass(item.risk)}`}>
                      {riskChipLabel(item.risk, labels)}
                    </span>
                  </td>
                  <td style={{ color: "#434654" }}>{item.reason}</td>
                  <td style={{ fontWeight: 600, color: "#004e32" }}>{item.suggestion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="a4-page page-break">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 48 }}>
          <h2 className="font-headline" style={{ margin: 0, fontSize: "1.875rem", fontWeight: 800, color: "#003d9b" }}>
            {labels.regsTitle}
          </h2>
          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.875rem", color: "#4f5f7b" }}>P. {nextPage()}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
          {regulationBlocks.map((block) => (
            <section key={block.key}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <SafetyCertificateOutlined style={{ color: "#003d9b", fontSize: 20 }} />
                <h3 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 700 }}>{block.title}</h3>
              </div>
              <div
                style={{
                  background: "#f1f3ff",
                  borderLeft: "4px solid #737685",
                  padding: 24,
                  borderRadius: 8,
                }}
              >
                <p style={{ margin: 0, fontSize: "0.875rem", lineHeight: 1.65, color: "#4f5f7b" }}>{block.body}</p>
                <p style={{ margin: "16px 0 0", fontSize: "0.75rem", fontWeight: 600, color: "#434654" }}>
                  {labels.impact}: {block.impact}
                </p>
              </div>
            </section>
          ))}
        </div>
      </div>

      <div className="a4-page">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 48 }}>
          <h2 className="font-headline" style={{ margin: 0, fontSize: "1.875rem", fontWeight: 800, color: "#003d9b" }}>
            {labels.appendixTitle}
          </h2>
          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: "0.875rem", color: "#4f5f7b" }}>P. {nextPage()}</span>
        </div>
        <div className="appendix-text" style={{ background: "#f1f3ff", borderRadius: 12, padding: 32, minHeight: 220 }}>
          <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{claimsText || labels.noClaims}</p>
        </div>
      </div>
    </div>
  );
}
